/**
 * play.runebound's deck validator: a thin adapter over the shared deck-legality engine
 * (`@play/deck-rules`, a generated copy of runebound-app's `packages/deck-rules`), so a deck is legal here
 * exactly when it is legal on runebound.gg. Every check and its rule citation lives in the engine.
 *
 * - Catalogue cards become `DeckRulesCard`s: the curated oracle text, keywords and the set codes of every
 *   printing of the card's rules name (TR 601.2.a: legal by name), cached per catalogue.
 * - Card ids resolve through `catalog.rulesCard`, so variant ids fold into their rules identity.
 * - The engine `DeckList` becomes a `DeckRulesDeck` (an empty legend id means no legend).
 */
import type { DeckList } from '@play/engine';
import {
  type DeckRulesCard,
  type DeckRulesDeck,
  type DeckValidation as EngineValidation,
  type ValidateOptions as EngineOptions,
  cardNameKey,
  championTag as engineChampionTag,
  legendDomains as engineLegendDomains,
  validateDeck as engineValidateDeck,
} from '@play/deck-rules';
import type { Catalog } from '../catalog';
import type { CatalogCard } from '../schema';

export type { DeckCounts } from '@play/deck-rules';

/** Format and live ban list for `validateDeck`. */
export type ValidateOptions = EngineOptions;

/** The validator verdict: `ok` when there are no issues. */
export type DeckValidation = EngineValidation;

/** The legend's champion tag (CR 133.8.b): the tag naming its champion, else its last tag. */
export function championTag(legend: CatalogCard): string | null {
  return engineChampionTag(legend);
}

/** The legend's domain identity (CR 103.2.b.2), without Colorless. */
export function legendDomains(legend: CatalogCard): string[] {
  return engineLegendDomains(legend);
}

interface RulesIndex {
  setsByName: Map<string, string[]>;
  cards: Map<string, DeckRulesCard>;
}

const indexCache = new WeakMap<Catalog, RulesIndex>();

function rulesIndex(catalog: Catalog): RulesIndex {
  const cached = indexCache.get(catalog);
  if (cached) return cached;
  const sets = new Map<string, Set<string>>();
  for (const printing of catalog.data.printings) {
    const card = catalog.rulesCard(printing.cardId);
    if (!card) continue;
    const key = cardNameKey(card.name);
    let codes = sets.get(key);
    if (!codes) {
      codes = new Set();
      sets.set(key, codes);
    }
    codes.add(printing.setCode);
  }
  const setsByName = new Map([...sets].map(([key, codes]) => [key, [...codes].sort()]));
  const index: RulesIndex = { setsByName, cards: new Map() };
  indexCache.set(catalog, index);
  return index;
}

/** A catalogue card as the engine sees it. */
export function toRulesCard(card: CatalogCard, catalog: Catalog): DeckRulesCard {
  const index = rulesIndex(catalog);
  const cached = index.cards.get(card.id);
  if (cached) return cached;
  const rules: DeckRulesCard = {
    id: card.id,
    name: card.name,
    type: card.type,
    supertype: card.supertype,
    domains: card.domains,
    tags: card.tags,
    text: card.oracle,
    keywords: card.keywords,
    sets: index.setsByName.get(cardNameKey(card.name)) ?? [],
  };
  index.cards.set(card.id, rules);
  return rules;
}

/** The engine's view of a normalised deck. */
export function toRulesDeck(deck: DeckList): DeckRulesDeck {
  return {
    legend: deck.legend.cardId ? deck.legend : null,
    champion: deck.champion,
    main: deck.main,
    runes: deck.runes,
    battlefields: deck.battlefields,
    sideboard: deck.sideboard,
  };
}

/** Validates a normalised deck against a format with the shared engine. */
export function validateDeck(deck: DeckList, catalog: Catalog, options: ValidateOptions = {}): DeckValidation {
  return engineValidateDeck(
    toRulesDeck(deck),
    (cardId) => {
      const card = catalog.rulesCard(cardId);
      return card ? toRulesCard(card, catalog) : null;
    },
    options,
  );
}
